/**
 * CategoryTrendsTab — analytics sub-tab shown when a category is selected.
 *
 * Sections:
 *   1. KPI strip (total items, revenue, avg items/day, busiest day)
 *   2. Day-of-week average sales (bar chart)
 *   3. Daily sales pivot table (date columns × item rows)
 *   4. Peak hours heatmap
 *   5. Top movers — items with biggest day-to-day variance
 *
 * All data derived from catDetail.ticketList (each ticket has .time ISO string + .lines[]).
 */
import React, { useMemo, useState } from 'react';
import { fmt$, fmtN } from '../../../utils/formatUtils';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_NAMES_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function dateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function shortDateLabel(iso) {
  const [, m, d] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(m) - 1]} ${parseInt(d)}`;
}
function dayOfWeek(iso) {
  return new Date(iso + 'T12:00:00').getDay();
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function CategoryTrendsTab({ catDetail }) {
  const [pivotMetric, setPivotMetric] = useState('qty'); // 'qty' | 'revenue'
  const [pivotSort, setPivotSort] = useState('total'); // 'total' | 'name'
  const [pivotSearch, setPivotSearch] = useState('');

  // ── Parse tickets into per-day, per-item buckets ───────────────────────────
  const analysis = useMemo(() => {
    const tickets = catDetail.ticketList || [];
    if (!tickets.length) return null;

    // dayMap: { '2026-05-28': { items: { 'Chicken 555': { qty, revCents } }, tickets: count, revCents } }
    const dayMap = {};
    // itemTotals: { 'Chicken 555': { qty, revCents, days: Set } }
    const itemTotals = {};
    // hourMap: [0..23] → { count, revCents }
    const hourMap = Array.from({ length: 24 }, () => ({ count: 0, revCents: 0 }));

    tickets.forEach(tkt => {
      const dt = tkt.time ? new Date(tkt.time) : null;
      if (!dt || isNaN(dt)) return;
      const dk = dateKey(dt);
      const hr = dt.getHours();
      hourMap[hr].count++;
      hourMap[hr].revCents += tkt.revenueCents || 0;

      if (!dayMap[dk]) dayMap[dk] = { items: {}, tickets: 0, revCents: 0, itemQty: 0 };
      dayMap[dk].tickets++;
      dayMap[dk].revCents += tkt.revenueCents || 0;

      (tkt.lines || []).forEach(line => {
        const name = line.name;
        const qty = line.qty || 1;
        const rev = line.revenueCents || 0;

        // Day-level
        if (!dayMap[dk].items[name]) dayMap[dk].items[name] = { qty: 0, revCents: 0 };
        dayMap[dk].items[name].qty += qty;
        dayMap[dk].items[name].revCents += rev;
        dayMap[dk].itemQty += qty;

        // Item totals
        if (!itemTotals[name]) itemTotals[name] = { qty: 0, revCents: 0, days: new Set() };
        itemTotals[name].qty += qty;
        itemTotals[name].revCents += rev;
        itemTotals[name].days.add(dk);
      });
    });

    // Sorted date keys
    const dates = Object.keys(dayMap).sort();
    const totalDays = dates.length;

    // Day-of-week aggregation
    const dowAgg = Array.from({ length: 7 }, () => ({ qty: 0, revCents: 0, tickets: 0, dayCount: 0 }));
    dates.forEach(dk => {
      const dow = dayOfWeek(dk);
      dowAgg[dow].qty += dayMap[dk].itemQty;
      dowAgg[dow].revCents += dayMap[dk].revCents;
      dowAgg[dow].tickets += dayMap[dk].tickets;
      dowAgg[dow].dayCount++;
    });
    const dowAvg = dowAgg.map((d, i) => ({
      day: DAY_NAMES[i],
      dayFull: DAY_NAMES_FULL[i],
      avgQty: d.dayCount > 0 ? d.qty / d.dayCount : 0,
      avgRevenue: d.dayCount > 0 ? d.revCents / d.dayCount / 100 : 0,
      avgTickets: d.dayCount > 0 ? d.tickets / d.dayCount : 0,
      totalQty: d.qty,
      totalRevenue: d.revCents / 100,
      occurrences: d.dayCount,
    }));

    // Item list sorted by total qty
    const items = Object.entries(itemTotals)
      .map(([name, v]) => ({
        name,
        totalQty: v.qty,
        totalRevCents: v.revCents,
        dayCount: v.days.size,
        avgPerDay: totalDays > 0 ? v.qty / totalDays : 0,
      }))
      .sort((a, b) => b.totalQty - a.totalQty);

    // Top movers: items with highest coefficient of variation (day-to-day)
    const movers = items.map(item => {
      const dailyQtys = dates.map(dk => dayMap[dk].items[item.name]?.qty || 0);
      const mean = dailyQtys.reduce((s, v) => s + v, 0) / (dailyQtys.length || 1);
      const variance = dailyQtys.reduce((s, v) => s + (v - mean) ** 2, 0) / (dailyQtys.length || 1);
      const stdDev = Math.sqrt(variance);
      const maxDay = dates[dailyQtys.indexOf(Math.max(...dailyQtys))];
      const minDay = dates[dailyQtys.indexOf(Math.min(...dailyQtys))];
      return { ...item, mean, stdDev, cv: mean > 0 ? stdDev / mean : 0, maxDay, minDay, max: Math.max(...dailyQtys), min: Math.min(...dailyQtys) };
    }).filter(m => m.dayCount >= 2).sort((a, b) => b.cv - a.cv);

    // Busiest day
    const busiestDate = dates.reduce((best, dk) => dayMap[dk].itemQty > (dayMap[best]?.itemQty || 0) ? dk : best, dates[0]);
    // Best revenue day
    const bestRevDate = dates.reduce((best, dk) => dayMap[dk].revCents > (dayMap[best]?.revCents || 0) ? dk : best, dates[0]);
    // Peak hour
    const peakHour = hourMap.reduce((best, h, i) => h.count > best.count ? { hour: i, ...h } : best, { hour: 0, count: 0, revCents: 0 });

    const totalQty = Object.values(itemTotals).reduce((s, v) => s + v.qty, 0);
    const totalRevCents = Object.values(itemTotals).reduce((s, v) => s + v.revCents, 0);

    return {
      dayMap, dates, totalDays, dowAvg, items, movers, hourMap,
      busiestDate, bestRevDate, peakHour, totalQty, totalRevCents,
    };
  }, [catDetail]);

  // Pivot table items (filtered + sorted) — must be before early return (hooks rule)
  const pivotItems = useMemo(() => {
    if (!analysis) return [];
    let list = analysis.items;
    if (pivotSearch.trim()) {
      const q = pivotSearch.toLowerCase();
      list = list.filter(item => item.name.toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => {
      if (pivotSort === 'name') return a.name.localeCompare(b.name);
      return b.totalQty - a.totalQty;
    });
  }, [analysis, pivotSearch, pivotSort]);

  if (!analysis || analysis.dates.length === 0) {
    return <div className="sq__empty" style={{ padding: '40px 20px', textAlign: 'center' }}>No ticket data available for trend analysis.</div>;
  }

  const { dayMap, dates, totalDays, dowAvg, items, movers, hourMap, busiestDate, bestRevDate, peakHour, totalQty, totalRevCents } = analysis;

  const maxDowQty = Math.max(...dowAvg.map(d => d.avgQty), 1);
  const maxDowRev = Math.max(...dowAvg.map(d => d.avgRevenue), 1);
  const maxHourCount = Math.max(...hourMap.map(h => h.count), 1);

  return (
    <div className="sq__trends">

      {/* ── KPI Strip ── */}
      <div className="sq__trends-kpis">
        <div className="sq__tkpi">
          <div className="sq__tkpi-val">{fmtN(totalQty)}</div>
          <div className="sq__tkpi-lbl">Total Items</div>
        </div>
        <div className="sq__tkpi sq__tkpi--green">
          <div className="sq__tkpi-val">{fmt$(totalRevCents / 100)}</div>
          <div className="sq__tkpi-lbl">Total Revenue</div>
        </div>
        <div className="sq__tkpi">
          <div className="sq__tkpi-val">{totalDays}</div>
          <div className="sq__tkpi-lbl">Days in Range</div>
        </div>
        <div className="sq__tkpi">
          <div className="sq__tkpi-val">{totalDays > 0 ? Math.round(totalQty / totalDays) : 0}</div>
          <div className="sq__tkpi-lbl">Avg Items/Day</div>
        </div>
        <div className="sq__tkpi sq__tkpi--indigo">
          <div className="sq__tkpi-val">{shortDateLabel(busiestDate)}</div>
          <div className="sq__tkpi-lbl">Busiest Day ({fmtN(dayMap[busiestDate].itemQty)} items)</div>
        </div>
        <div className="sq__tkpi sq__tkpi--green">
          <div className="sq__tkpi-val">{shortDateLabel(bestRevDate)}</div>
          <div className="sq__tkpi-lbl">Best Revenue ({fmt$(dayMap[bestRevDate].revCents / 100)})</div>
        </div>
        <div className="sq__tkpi sq__tkpi--amber">
          <div className="sq__tkpi-val">{String(peakHour.hour).padStart(2, '0')}:00</div>
          <div className="sq__tkpi-lbl">Peak Hour ({peakHour.count} tickets)</div>
        </div>
        <div className="sq__tkpi">
          <div className="sq__tkpi-val">{items.length}</div>
          <div className="sq__tkpi-lbl">Unique Items</div>
        </div>
      </div>

      {/* ── Day-of-Week Averages ── */}
      <div className="sq__trends-section">
        <h3 className="sq__trends-title">📅 Day-of-Week Averages</h3>
        <p className="sq__trends-sub">Average daily performance based on {totalDays} day{totalDays !== 1 ? 's' : ''} in the selected range</p>
        <div className="sq__dow-grid">
          {dowAvg.map((d, i) => {
            const qtyPct = (d.avgQty / maxDowQty) * 100;
            const revPct = (d.avgRevenue / maxDowRev) * 100;
            const isBest = d.avgQty === Math.max(...dowAvg.map(x => x.avgQty));
            return (
              <div key={i} className={`sq__dow-card${d.occurrences === 0 ? ' sq__dow-card--empty' : ''}${isBest ? ' sq__dow-card--best' : ''}`}>
                <div className="sq__dow-day">{d.day}</div>
                <div className="sq__dow-bars">
                  <div className="sq__dow-bar-wrap">
                    <div className="sq__dow-bar sq__dow-bar--qty" style={{ height: `${qtyPct}%` }} title={`Avg ${d.avgQty.toFixed(1)} items`} />
                  </div>
                  <div className="sq__dow-bar-wrap">
                    <div className="sq__dow-bar sq__dow-bar--rev" style={{ height: `${revPct}%` }} title={`Avg ${fmt$(d.avgRevenue)}`} />
                  </div>
                </div>
                <div className="sq__dow-stats">
                  <span className="sq__dow-stat">{d.avgQty.toFixed(1)} items</span>
                  <span className="sq__dow-stat sq__dow-stat--rev">{fmt$(d.avgRevenue)}</span>
                  <span className="sq__dow-stat sq__dow-stat--tkt">{d.avgTickets.toFixed(1)} tkt</span>
                </div>
                {d.occurrences > 0 && <div className="sq__dow-occ">{d.occurrences} {d.dayFull}{d.occurrences !== 1 ? 's' : ''}</div>}
              </div>
            );
          })}
        </div>
        <div className="sq__dow-legend">
          <span className="sq__dow-leg"><span className="sq__dow-leg-dot sq__dow-leg-dot--qty" /> Avg Items</span>
          <span className="sq__dow-leg"><span className="sq__dow-leg-dot sq__dow-leg-dot--rev" /> Avg Revenue</span>
        </div>
      </div>

      {/* ── Peak Hours Heatmap ── */}
      <div className="sq__trends-section">
        <h3 className="sq__trends-title">🕐 Peak Hours</h3>
        <p className="sq__trends-sub">Ticket volume by hour of day</p>
        <div className="sq__hours-grid">
          {hourMap.map((h, i) => {
            const intensity = h.count / maxHourCount;
            return (
              <div key={i} className="sq__hour-cell"
                style={{ '--heat': intensity }}
                title={`${String(i).padStart(2, '0')}:00 — ${h.count} tickets, ${fmt$(h.revCents / 100)}`}>
                <span className="sq__hour-lbl">{String(i).padStart(2, '0')}</span>
                {h.count > 0 && <span className="sq__hour-cnt">{h.count}</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Daily Sales Pivot Table ── */}
      <div className="sq__trends-section">
        <h3 className="sq__trends-title">📊 Daily Sales Breakdown</h3>
        <p className="sq__trends-sub">
          Each column is a day. Scroll horizontally for more dates.
          <span className="sq__trends-controls">
            <span className="sq__pivot-search-wrap">
              <input
                className="sq__pivot-search"
                type="search"
                placeholder="Filter items…"
                value={pivotSearch}
                onChange={e => setPivotSearch(e.target.value)}
              />
              {pivotSearch && <button className="sq__pivot-search-clear" onClick={() => setPivotSearch('')}>✕</button>}
            </span>
            <button className={`sq__trends-pill${pivotMetric === 'qty' ? ' active' : ''}`} onClick={() => setPivotMetric('qty')}>Qty</button>
            <button className={`sq__trends-pill${pivotMetric === 'revenue' ? ' active' : ''}`} onClick={() => setPivotMetric('revenue')}>Revenue</button>
            <span className="sq__trends-sep">|</span>
            <button className={`sq__trends-pill${pivotSort === 'total' ? ' active' : ''}`} onClick={() => setPivotSort('total')}>By Total</button>
            <button className={`sq__trends-pill${pivotSort === 'name' ? ' active' : ''}`} onClick={() => setPivotSort('name')}>A→Z</button>
          </span>
        </p>
        <div className="sq__pivot-wrap">
          <table className="sq__pivot-table">
            <thead>
              <tr>
                <th className="sq__pivot-item-th">Item</th>
                <th className="sq__pivot-total-th">Total</th>
                <th className="sq__pivot-avg-th">Avg/Day</th>
                {dates.map(dk => {
                  const dow = DAY_NAMES[dayOfWeek(dk)];
                  return (
                    <th key={dk} className="sq__pivot-date-th">
                      <span className="sq__pivot-date-dow">{dow}</span>
                      <span className="sq__pivot-date-label">{shortDateLabel(dk)}</span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {pivotItems.slice(0, 30).map(item => {
                const vals = dates.map(dk => {
                  const d = dayMap[dk].items[item.name];
                  return pivotMetric === 'qty' ? (d?.qty || 0) : (d?.revCents || 0) / 100;
                });
                const total = pivotMetric === 'qty' ? item.totalQty : item.totalRevCents / 100;
                const avg = totalDays > 0 ? total / totalDays : 0;
                const maxVal = Math.max(...vals, 1);
                return (
                  <tr key={item.name}>
                    <td className="sq__pivot-item-td">{item.name}</td>
                    <td className="sq__pivot-total-td">{pivotMetric === 'qty' ? fmtN(total) : fmt$(total)}</td>
                    <td className="sq__pivot-avg-td">{pivotMetric === 'qty' ? avg.toFixed(1) : fmt$(avg)}</td>
                    {vals.map((v, vi) => {
                      const intensity = v / maxVal;
                      return (
                        <td key={vi} className="sq__pivot-cell" style={{ '--cell-heat': intensity }}>
                          {v > 0 ? (pivotMetric === 'qty' ? v : fmt$(v)) : <span className="sq__pivot-zero">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="sq__pivot-foot">
                <td className="sq__pivot-item-td"><strong>Day Total</strong></td>
                <td className="sq__pivot-total-td">
                  <strong>{pivotMetric === 'qty' ? fmtN(totalQty) : fmt$(totalRevCents / 100)}</strong>
                </td>
                <td className="sq__pivot-avg-td" />
                {dates.map(dk => {
                  const dayTotal = pivotMetric === 'qty'
                    ? dayMap[dk].itemQty
                    : dayMap[dk].revCents / 100;
                  return <td key={dk} className="sq__pivot-cell sq__pivot-foot-cell"><strong>{pivotMetric === 'qty' ? fmtN(dayTotal) : fmt$(dayTotal)}</strong></td>;
                })}
              </tr>
            </tfoot>
          </table>
        </div>
        {pivotItems.length > 30 && <p className="sq__trends-note">Showing top 30 of {pivotItems.length} items</p>}
        {pivotSearch && <p className="sq__trends-note">{pivotItems.length} item{pivotItems.length !== 1 ? 's' : ''} matching "{pivotSearch}"</p>}
      </div>

      {/* ── Top Movers (high variance items) ── */}
      {movers.length > 0 && (
        <div className="sq__trends-section">
          <h3 className="sq__trends-title">📈 Most Variable Items</h3>
          <p className="sq__trends-sub">Items with the biggest day-to-day swings — useful for demand planning</p>
          <div className="sq__movers-grid">
            {movers.slice(0, 8).map(m => (
              <div key={m.name} className="sq__mover-card">
                <div className="sq__mover-name">{m.name}</div>
                <div className="sq__mover-stats">
                  <span className="sq__mover-range">{m.min}–{m.max}/day</span>
                  <span className="sq__mover-avg">avg {m.mean.toFixed(1)}</span>
                </div>
                <div className="sq__mover-bar-wrap">
                  <div className="sq__mover-min" style={{ left: `${(m.min / m.max) * 100}%` }} />
                  <div className="sq__mover-avg-line" style={{ left: `${(m.mean / m.max) * 100}%` }} />
                  <div className="sq__mover-fill" style={{ width: '100%' }} />
                </div>
                <div className="sq__mover-detail">
                  Best: {shortDateLabel(m.maxDay)} ({m.max}) · Lowest: {shortDateLabel(m.minDay)} ({m.min})
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Daily Totals Summary Row ── */}
      <div className="sq__trends-section">
        <h3 className="sq__trends-title">📋 Daily Summary</h3>
        <p className="sq__trends-sub">Quick view of each day's totals</p>
        <div className="sq__daily-summary">
          {dates.map(dk => {
            const d = dayMap[dk];
            const dow = DAY_NAMES[dayOfWeek(dk)];
            return (
              <div key={dk} className="sq__dsum-card">
                <div className="sq__dsum-date">{dow} {shortDateLabel(dk)}</div>
                <div className="sq__dsum-row">
                  <span className="sq__dsum-metric">{fmtN(d.itemQty)} items</span>
                  <span className="sq__dsum-metric sq__dsum-metric--rev">{fmt$(d.revCents / 100)}</span>
                  <span className="sq__dsum-metric sq__dsum-metric--tkt">{d.tickets} tkt</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
